import React, { useState, useMemo, useEffect, useCallback} from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
// import jsPDF from 'jspdf';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Tooltip } from 'react-tooltip';
import '../styles/DoctorDashboard.css'; // Updated styles
import SpeechToTextInput from '../../SpeechRecognition/speechRecognition.component';
import FormatUrl from '../../../utils/UrlFormatter';
import { notifySuccess, notifyError } from '../../../utils/toastNotifications';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTrashAlt, faCalendarAlt, faCreditCard, faHourglass } from '@fortawesome/free-solid-svg-icons'; 
import { getClinicId } from '../../../utils/config';

const localizer = momentLocalizer(moment);

const DoctorDashboard = ({ doctorName, user, logged_in }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const customMessages = {
    month: 'Month',
    week: 'Week',
    day: 'Day',
    agenda: 'Summary', // Change 'Agenda' to 'Summary'
  };

  const [viewMode, setViewMode] = useState('calendar');
  const [filter, setFilter] = useState('All');
  // TODO - use setSearchTerm state below when making search work
  const [searchTerm] = useState(''); // search term for patient name
  const [selectedAppointment, setSelectedAppointment] = useState(null); 
  const [appointments, setAppointments] = useState([]); 
  const [isModalOpen, setIsModalOpen] = useState(false); 
  const [doctorNotes, setDoctorNotes] = useState(''); 
  const [prescription, setPrescription] = useState('');
  const [symptoms, setSymptoms] = useState('');
  const [followUpDetails, setFollowUpDetails] = useState('');
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isEmailModalOpen, setEmailModalOpen] = useState(false);
  const [patientEmail, setPatientEmail] = useState('');
  const [duration, setDuration] = useState('');
  // const [cost, setCost] = useState('');
  const [techniques, setTechniques] = useState('');
  const [comments, setComments] = useState('');
  const [selfCare, setSelfCare] = useState('');
  const [fetchingDoctorNotes, setFetchingDoctorNotes] = useState(false);

  // const [signatureType, setSignatureType] = useState('typed'); 
  // const [typedSignature, setTypedSignature] = useState('');
  // const canvasRef = useRef(null);
  // const [isDrawing, setIsDrawing] = useState(false);

  // const clinicIdScope = getClinicId(user)

  const fetchAppointments = useCallback(async () => {
    try {
      const doctorId = user?.doctor_id;
      //todo - need to change this associated_clincs logic later
      // const clinicId = user?.associated_clincs?.[0]?.id; 
      const clinicId = getClinicId(user) 
  
      if (!doctorId || !clinicId) {
        console.error('Missing doctor_id or clinic_id');
        return;
      }
  
      // Fetch CSRF Token
      const csrfResponse = await fetch(FormatUrl('/csrf-token/'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
  
      // Fetch Appointments API
      const apiUrl = FormatUrl(`/get-appointments/?clinic_id=${clinicId}&doctor_id=${doctorId}`);
  
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      });
  
      if (!response.ok) throw new Error('Failed to fetch appointments.');
  
      const data = await response.json();
      if (data.status === 'success') {
        // Filter bookings with statuses BO, AP, CA only
        const validStatuses = ['BO', 'AP', 'CA', 'CO', 'PW', 'PC'];
        const filteredAppointments = data.appointments.filter(appointment =>
          validStatuses.includes(appointment.status)
        );
  
        // Map the filtered appointments
        const mappedAppointments = filteredAppointments.map((appointment) => ({
          id: appointment.appointment_id, // Unique ID
          patientId: appointment.patient_id,
          slotId: appointment.slot_id,
          doctorId: appointment.doctor_id,
          start: new Date(appointment.slot_start_time),
          end: new Date(appointment.slot_end_time),
          status: appointment.status, // Keep status as is
          clinicId: appointment.clinic_id,
          patientName: `${appointment.patient_first_name} ${appointment.patient_last_name}`,
          stationNumber: appointment.station_number,
        }));
  
        setAppointments(mappedAppointments);
      } else {
        console.error('Error fetching appointments:', data.error);
        setAppointments([]);
      }
    } catch (error) {
      console.error('Error in fetchAppointments:', error.message);
      setAppointments([]);
    }
  }, [user]);

  
  useEffect(() => {
    if (user?.doctor_id && user?.associated_clincs?.[0]?.id) {
      fetchAppointments();
    }
  }, [user?.doctor_id, user?.associated_clincs, fetchAppointments]);
  
  const doctorSpecificAppointments = useMemo(() => {
    
    if (!user?.doctor_id) {
      console.error('Doctor ID is missing in user data.');
      return [];
    }
  
    const filtered = appointments.filter(
      (appointment) => appointment.doctorId === user.doctor_id
    );
  
    return filtered;
  }, [appointments, user?.doctor_id]);
  
  
  const filterAppointments = (appointments, filter, searchTerm) => {
    // Define status mappings
    const statusMappings = {
      All: ['BO', 'AP', 'CO', 'CA', 'PW', 'PC'], 
      Active: ['BO'],                
      approved: ['AP'],              
      completed: ['CO'],             
      cancelled: ['CA'], 
      practitionerincomplete: ['PW'], 
      paymentcompleted: ['PC'],            
    };
  
    const validStatuses = statusMappings[filter] || []; 
  
    return appointments.filter((appointment) => {
      const matchesStatus =
        filter === 'All' || validStatuses.includes(appointment.status);
  
      const matchesSearch = searchTerm
        ? appointment.patientName.toLowerCase().includes(searchTerm.toLowerCase())
        : true;
  
      return matchesStatus && matchesSearch;
    });
  };
  
  const filteredAppointmentsForCalendarView = useMemo(() => {
    return filterAppointments(doctorSpecificAppointments, filter, searchTerm);
  }, [doctorSpecificAppointments, filter, searchTerm]);
  
  const filteredAppointmentsForListView = useMemo(() => {
    return filterAppointments(doctorSpecificAppointments, filter, searchTerm);
  }, [doctorSpecificAppointments, filter, searchTerm]);
  
  const fetchDoctorNotes = useCallback(async (appointmentId) => {
    try {
      if (!appointmentId) {
        console.error("Missing appointment ID");
        return;
      }
  
      setFetchingDoctorNotes(true);
  
      // API call to get doctor notes via GET
      const apiUrl = FormatUrl(`/get-appointment-doctor-notes/?appointment_id=${appointmentId}`);
  
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
  
      if (!response.ok) throw new Error("Failed to fetch doctor notes.");
  
      const data = await response.json();
      if (data.status === "failure") throw new Error(data.error || "Unknown error");
  
      // Update SOAP fields
      setDoctorNotes(data.subjective || "N/A");
      setPrescription(data.objective || "N/A");
      setSymptoms(data.assessment || "N/A");
      setFollowUpDetails(data.plan || "N/A");
  
      // Safely parse and update 'Other' fields
      let other = {};
      try {
        if (data.other) {
          other = typeof data.other === "string" ? JSON.parse(data.other) : data.other;
        }
      } catch (parseError) {
        console.warn("Failed to parse 'other' field:", parseError);
      }
  
      setDuration(other.duration_of_massage || "N/A");
      // setCost(other.cost || "N/A");
      setTechniques(other.techniques_used || "N/A");
      setComments(other.comments || "N/A");
      setSelfCare(other.self_care_recommendations || "N/A");
  
    } catch (error) {
      console.error("Error in fetchDoctorNotes:", error.message);
    } finally {
      setFetchingDoctorNotes(false);
    }
  }, []);
  // Open appointment modal only for approved appointments
  const handleEventClick = async (event) => {
  
    if (['AP', 'CO', 'PW'].includes(event.status)) {
      // Set initial selected appointment
      setSelectedAppointment(event);
  
      // For 'AP' status: Allow editing and load details from localStorage
      if (event.status === 'AP' || event.status === 'PW') {
        const storedAppointment = JSON.parse(localStorage.getItem(`appointment_${event.id}`));
  
        if (storedAppointment) {
          setDoctorNotes(storedAppointment.doctorNotes || '');
          setPrescription(storedAppointment.prescription || '');
          setSymptoms(storedAppointment.symptoms || '');
          setFollowUpDetails(storedAppointment.followUpDetails || '');
        } else {
          // Clear all fields if no localStorage data
          setDoctorNotes('');
          setPrescription('');
          setSymptoms('');
          setFollowUpDetails('');
        }

         // Clear treatment summary fields
          setDuration('');
          setTechniques('');
          setComments('');
          setSelfCare('');
        setIsReadOnly(false); // Editable fields for approved appointments
      }
  
      // For 'CO' status: Set fields as read-only
      if (event.status === 'CO') {
        setIsReadOnly(true); // Read-only fields for completed appointments
        // Fetch completed data from backend
      await fetchDoctorNotes(event.id);
      }
  
      setIsModalOpen(true); // Open the modal
    } else {
      console.log('Modal not allowed for this status:', event.status);
    }
  };

  
  const handleMarkAsCompleted = async () => {
    try {
      const clinicId = selectedAppointment?.clinicId;
      const doctorId = selectedAppointment?.doctorId;
      const patientId = selectedAppointment?.patientId;
      const appointmentId = selectedAppointment?.id;
  
      if (!clinicId || !doctorId || !patientId || !appointmentId) {
        notifyError("Missing necessary information to mark as completed.");
        return;
      }
  
      // Fetch CSRF Token
      const csrfResponse = await fetch(FormatUrl('/csrf-token/'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
  
      const apiUrl = FormatUrl(`/complete-appointment/`);
      const formData = new URLSearchParams();
      formData.append('appointment_id', appointmentId);
      formData.append('clinic_id', clinicId);
      formData.append('practitioner_id', doctorId);
      formData.append('patient_id', patientId);
  
      // SOAP fields
      if (doctorNotes) formData.append('subjective', doctorNotes);
      if (prescription) formData.append('objective', prescription);
      if (symptoms) formData.append('assessment', symptoms);
      if (followUpDetails) formData.append('plan', followUpDetails);
  
      // Add "Other" section as JSON string
      const other = {
        duration_of_massage: duration,
        // cost: cost,
        techniques_used: techniques,
        comments: comments,
        self_care_recommendations: selfCare,
      };
      formData.append('other', JSON.stringify(other));
  
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: formData,
      });
  
      if (response.ok) {
        const result = await response.json();
  
        if (['CO', 'PC'].includes(result.status)) {
          notifySuccess('Appointment successfully marked as completed.');
        
          setAppointments((prevAppointments) =>
            prevAppointments.map((appointment) =>
              appointment.id === appointmentId
                ? { ...appointment, status: result.status }
                : appointment
            )
          );
        
          setIsModalOpen(false);
        } else {
          console.error('Failed to mark appointment as completed:', result.error);
          notifyError(result.error || 'Failed to mark appointment as completed.');
        }
      } else {
        console.error('API Error:', response.statusText);
        notifyError('Error occurred while marking appointment as completed.');
      }
    } catch (error) {
      console.error('Error in handleMarkAsCompleted:', error.message);
      notifyError('An unexpected error occurred.');
    }
  };


  const getFullName = (user) => {
    if (!user || !user.first_name || !user.last_name) {
      return ""; // Return empty if user is not available
    }
    return `${user.first_name} ${user.last_name}`;
  };

  useEffect(() => {
    // Ensure the logged-in user's doctor_id matches the one in the route
    const currentDoctorId = window.location.pathname.split("/").pop();
    if (currentDoctorId !== String(user?.doctor_id)) {
      navigate("/dashboard"); // Redirect to unauthorized page
    }
  }, [user, navigate]);
  
  return (
      <div className="custom-doctor-dashboard-container">
        <h2 style={{ color: 'var(--clr-grey-1)' }}>{getFullName(user)} {t("practitioner_dashboard.dashboard")}</h2>

        {/* Legend for Color Codes */}
        <div className="custom-legend">
          <p>
            <FontAwesomeIcon icon={faCalendarAlt} style={{ color: "#e07a5f", marginRight: '8px' }} />
            {t("practitioner_dashboard.booked")} (BO)
          </p>
          <p>
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#fbc02d", marginRight: '8px' }} />
            {t("practitioner_dashboard.approved")} (AP)
          </p>
          <p>
            <FontAwesomeIcon icon={faHourglass} style={{ color: "#0288d1", marginRight: '8px' }} />
            {t("practitioner_dashboard.payment_completed_practitioner_incomplete")} (PW)
          </p>
          <p>
            <FontAwesomeIcon icon={faCreditCard} style={{ color: "#8e24aa", marginRight: '8px' }} />
            {t("practitioner_dashboard.payment_completed")} (PC)
          </p>
          <p>
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#43a047", marginRight: '8px' }} />
            {t("practitioner_dashboard.completed")} (CO)
          </p>
          <p>
            <FontAwesomeIcon icon={faTrashAlt} style={{ color: "#e07a5f", marginRight: '8px' }} />
            {t("practitioner_dashboard.cancelled")} (CA)
          </p>
        </div>


        {/* Filters and Search */}
        <div className="custom-dashboard-controls">
          <div className="custom-filter-pills">
            <button className={`custom-pill-button ${filter === 'All' ? 'active' : ''}`} onClick={() => setFilter('All')}>{t("practitioner_dashboard.all")}</button>
            <button className={`custom-pill-button ${filter === 'Active' ? 'active' : ''}`} onClick={() => setFilter('Active')}>{t("practitioner_dashboard.booked")}</button>
            <button className={`custom-pill-button ${filter === 'approved' ? 'active' : ''}`} onClick={() => setFilter('approved')}>{t("practitioner_dashboard.approved")}</button>
            <button className={`custom-pill-button ${filter === 'completed' ? 'active' : ''}`} onClick={() => setFilter('completed')}>{t("practitioner_dashboard.completed")}</button>
            {/* <button className={`custom-pill-button ${filter === 'deleted' ? 'active' : ''}`} onClick={() => setFilter('deleted')}>Deleted</button> */}
            <button className={`custom-pill-button ${filter === 'cancelled' ? 'active' : ''}`} onClick={() => setFilter('cancelled')}>{t("practitioner_dashboard.cancelled")}</button> 
            <button className={`custom-pill-button ${filter === 'practitionerincomplete' ? 'active' : ''}`} onClick={() => setFilter('practitionerincomplete')}>Practitioner Incomplete</button>
            {/* <button
            className={`custom-pill-button ${filter === 'paymentcompleted' ? 'active' : ''}`}
            onClick={() => setFilter('paymentcompleted')}
          >
            Payment Completed
          </button> */}

          </div>
        </div>

        {/* View Toggle */}
        <div className="custom-view-toggle">
          <button className={`custom-view-button ${viewMode === 'calendar' ? 'active' : ''}`} onClick={() => setViewMode('calendar')}>{t("practitioner_dashboard.calendar_view")}</button>
          <button className={`custom-view-button ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>{t("practitioner_dashboard.list_view")}</button>
        </div>

        {/* Calendar View */}
        {viewMode === 'calendar' ? (
          <div className="doctor-calendar">
        <Calendar
            localizer={localizer}
            events={filteredAppointmentsForCalendarView} // Use mappedAppointments for events
            startAccessor="start"
            endAccessor="end"
            style={{ height: 600 }}
            onSelectEvent={handleEventClick} // Handle event click for modal
            views={['month', 'week', 'day', 'agenda']}  // Supported views
            messages={customMessages}
            eventPropGetter={(event) => {
              let backgroundImage;
              let color = 'white'; // default text color
            
              switch (event.status) {
                case 'BO': // Booked
                  backgroundImage = 'linear-gradient(135deg, #e07a5f, #b95441)'; // 🟧 Coral-Orange
                  break;
                case 'AP': // Approved
                  backgroundImage = 'linear-gradient(135deg, #fdd835, #fbc02d)'; // 🟨 Amber
                  color = '#2c2c2c'; // dark text for readability
                  break;
                case 'PW': // Payment Completed (Practitioner Incomplete)
                  backgroundImage = 'linear-gradient(135deg, #42a5f5, #1e88e5)'; // 🟦 Blue
                  break;
                case 'CO': // Completed
                  backgroundImage = 'linear-gradient(135deg, #66bb6a, #43a047)'; // 🟩 Green
                  break;
                case 'CA': // Cancelled
                  backgroundImage = 'linear-gradient(135deg, #ef5350, #e53935)'; // 🟥 Red
                  break;
                case 'PC': 
                  backgroundImage = 'linear-gradient(135deg, #6a1b9a, #8e24aa)'; // 🟪 Purple
                  break;
                default:
                  backgroundImage = 'linear-gradient(135deg, #bdbdbd, #9e9e9e)'; // Neutral Grey
                  color = '#2c2c2c';
              }
            
              return {
                style: {
                  backgroundImage,
                  color,
                  borderRadius: '5px',
                  padding: '0.5rem',
                  animation: 'bounceIn 0.5s ease-in-out',
                },
              };
            }}
            
            components={{
              event: ({ event }) => (
                <div
                  data-tooltip-id={`tooltip-${event.id}`}
                  data-tooltip-content={`
                    ${t("practitioner_dashboard.patient")}: ${event.patientName} | 
                    ${t("practitioner_dashboard.time")}: ${moment(event.start).format("h:mm A")} - ${moment(event.end).format("h:mm A")} | 
                    ${t("practitioner_dashboard.status")}: ${event.status}
                  `}
                >
                  {/* Icons per status */}
                  {event.status === 'BO' && (
                    <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '8px', color: '#e07a5f' }} />
                  )}
                  {event.status === 'AP' && (
                    <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '8px', color: '#fbc02d' }} />
                  )}
                  {event.status === 'PW' && (
                    <FontAwesomeIcon icon={faHourglass} style={{ marginRight: '8px', color: '#0288d1' }} />
                  )}
                  {event.status === 'CO' && (
                    <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '8px', color: '#43a047' }} />
                  )}
                  {event.status === 'CA' && (
                    <FontAwesomeIcon icon={faTrashAlt} style={{ marginRight: '8px', color: '#e07a5f' }} />
                  )}
                  {event.status === 'PC' && (
                    <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '8px', color: '#1565c0' }} />
                  )}

                  <strong>{event.patientName}</strong>
                  <Tooltip id={`tooltip-${event.id}`} />
                </div>
              )
            }}
            
          />

          </div>

        ) : (
          <ul className="custom-event-list">
            {filteredAppointmentsForListView.length > 0 ? (
              filteredAppointmentsForListView.map((appointment) => (
                <li key={appointment.id} className="custom-event-list-item">
                  <p><strong>{t("practitioner_dashboard.patient")}:</strong> {appointment.patientName}</p>
                  <p><strong>{t("practitioner_dashboard.time")}:</strong> {moment(appointment.start).format('MMMM Do, h:mm A')}</p>
                  <p>
                    <strong>{t("practitioner_dashboard.status")}:</strong>
                    <span className={`custom-status-badge ${appointment.status}`}>
                      {appointment.status === "BO" && (
                        <>
                          <FontAwesomeIcon icon={faCalendarAlt} style={{ color: "#e07a5f", marginRight: '0.5rem' }} />
                          {t("practitioner_dashboard.booked")} (BO)
                        </>
                      )}

                      {appointment.status === "AP" && (
                        <>
                          <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#82b74b", marginRight: '0.5rem' }} />
                          {t("practitioner_dashboard.approved")} (AP)
                        </>
                      )}

                      {appointment.status === "CO" && (
                        <>
                          <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#4d79ff", marginRight: '0.5rem' }} />
                          {t("practitioner_dashboard.completed")} (CO)
                        </>
                      )}

                      {appointment.status === "CA" && (
                        <>
                          <FontAwesomeIcon icon={faTrashAlt} style={{ color: "#e07a5f", marginRight: '0.5rem' }} />
                          {t("practitioner_dashboard.cancelled")} (CA)
                        </>
                      )}

                      {appointment.status === "PW" && (
                        <>
                          <FontAwesomeIcon icon={faHourglass} style={{ color: "#0288d1", marginRight: '0.5rem' }} />
                          {t("practitioner_dashboard.payment_completed_practitioner_incomplete")} (PW)
                        </>
                      
                      )}
                      {appointment.status === "PC" && (
                        <>
                          <FontAwesomeIcon icon={faCreditCard} style={{ color: "#1565c0", marginRight: '0.5rem' }} />
                          Payment Completed (PC)
                        </>
                      )}

                    </span>
                  </p>

                  {/* Add a View Details button to trigger the modal */}
                  
                  {/* Show "View Details" button only for AP and CO statuses */}
                  {['AP', 'CO', 'PW'].includes(appointment.status) && (
                      <button
                          className="custom-view-details-btn"
                          onClick={() => handleEventClick(appointment)}
                      >
                          {t("practitioner_dashboard.view_details")}
                      </button>
                  )}        
                </li>
              ))
            ) : (
              <li className="custom-empty-container">
                <div className="custom-empty-message-box">
                  <p className="custom-empty-message">
                  {t("practitioner_dashboard.no_appointments_available")}
                  </p>
                </div>
              </li>
            )}
          </ul>
        )}

        {/* Combined Modal for Appointment Details and Intake Form */}
        {
          isModalOpen && (
            <div className="custom-appointment-modal">
              <div className="custom-appointment-modal-content">
                {/* Close Icon */}
                <div className="custom-close-icon-container" onClick={() => setIsModalOpen(false)}>
                  <span className="custom-close-icon">&times;</span> 
                </div>
                    <h3>{t("practitioner_dashboard.medical_summary_treatment_plan")}</h3>
                      <div className="custom-modal-sections">
                        {/* Left Section: Appointment Details + Intake Form */}
                        <div className="custom-left-section">
                          <div className="custom-appointment-section">
                            <h4>{t("practitioner_dashboard.appointment_details")}</h4>
                            <p><strong>{t("practitioner_dashboard.patient")}:</strong> {selectedAppointment.patientName}</p>
                            <p><strong>{t("practitioner_dashboard.time")}:</strong> {moment(selectedAppointment.start).format('MMMM Do, h:mm A')} - {moment(selectedAppointment.end).format('h:mm A')}</p>
                            <p><strong>{t("practitioner_dashboard.status")}:</strong> {selectedAppointment.status}</p>
                            {selectedAppointment.stationNumber && (
                              <p>
                                <strong>Room:</strong>{' '}
                                <span className="room-highlight-badge">
                                  Room {selectedAppointment.stationNumber}
                                </span>
                              </p>
                            )}

                            <div className="intake-form-info-wrapper">
                              <p className="intake-form-info-text">{t("practitioner_dashboard.view_intake_description")}</p>
                              <button
                                className="doctor-dashboard-btn custom-intake-form-btn"
                                onClick={() =>
                                  window.open(
                                    `/view-intake/${selectedAppointment.patientId}/${selectedAppointment.doctorId}`,
                                    "_blank"
                                  )
                                }
                              >
                                {t("practitioner_dashboard.view_intake_form")}
                              </button>
                            </div>
                            {selectedAppointment?.patientId && selectedAppointment?.doctorId && (
                              <div className="timeline-cta-box">
                                <p className="timeline-cta-text">
                                  To see patient’s appointment history and timeline –{" "}
                                  <span
                                    className="timeline-cta-link"
                                    onClick={() =>
                                      window.open(
                                        `/patient-timeline/${selectedAppointment.patientId}`,
                                        '_blank',
                                        'noopener,noreferrer'
                                      )
                                    }
                                  >
                                    Click here
                                  </span>
                                </p>
                              </div>
                            )}
                          </div>

                          {selectedAppointment.intakeForm && (
                            <div className="custom-intake-form-section">
                              <h4>{t("practitioner_dashboard.intake_form")}</h4>
                              <p><strong>{t("practitioner_dashboard.medical_history")}:</strong> {selectedAppointment.intakeForm.medicalHistory}</p>
                              {/* <p><strong>{t("practitioner_dashboard.current_medications")}:</strong> {selectedAppointment.intakeForm.currentMedications}</p>
                              <p><strong>{t("practitioner_dashboard.allergies")}:</strong> {selectedAppointment.intakeForm.allergies}</p> */}
                            </div>
                          )}
                        </div>

                        {/* Right Section: Doctor's Notes + Prescription */}
                        <div className="custom-right-section">
                        {fetchingDoctorNotes ? (
                          <p style={{ marginBottom: '1rem', color: 'var(--clr-grey-5)' }}>
                            {t("practitioner_dashboard.loading_notes") || "Loading doctor notes..."}
                          </p>
                        ) : (
                          <>
                          <div className="custom-doctor-notes-section">
                            <h4>{t("practitioner_dashboard.practitioner_notes")}</h4>
                            <div className="textarea-wrapper">
                            <textarea
                              value={doctorNotes}
                              onChange={(e) => setDoctorNotes(e.target.value)}
                              placeholder={t("practitioner_dashboard.add_notes")}
                              readOnly={isReadOnly} // Make this field read-only for completed status
                            />
                          <SpeechToTextInput
                                onChange={(transcribedText) => setDoctorNotes((prevNotes) => prevNotes + ' ' + transcribedText)} 
                                isReadOnly={isReadOnly} // Disable speech-to-text when read-only
                            />

                            </div>
                          </div>

                          <div className="custom-additional-details">
                            <h4>{t("practitioner_dashboard.prescription")}</h4>
                            <div className="textarea-wrapper">
                            <textarea
                              value={prescription}
                              onChange={(e) => setPrescription(e.target.value)}
                              placeholder={t("practitioner_dashboard.add_prescription")}
                              readOnly={isReadOnly}
                            />
                            <SpeechToTextInput
                              onChange={(transcribedText) => setPrescription((prevNotes) => prevNotes + ' ' + transcribedText)}  
                              isReadOnly={isReadOnly}
                            />
                            </div>
                            <h4>{t("practitioner_dashboard.symptoms")}</h4>
                            <div className="textarea-wrapper">
                            <textarea
                              value={symptoms}
                              onChange={(e) => setSymptoms(e.target.value)}
                              placeholder={t("practitioner_dashboard.add_symptoms")}
                              readOnly={isReadOnly}
                            />
                            <SpeechToTextInput
                              onChange={(transcribedText) => setSymptoms((prevNotes) => prevNotes + ' ' + transcribedText)}  
                              isReadOnly={isReadOnly}
                            />
                            </div>
                            <h4>{t("practitioner_dashboard.follow_up_details")}</h4>
                            <div className="textarea-wrapper">
                            <textarea
                              value={followUpDetails}
                              onChange={(e) => setFollowUpDetails(e.target.value)}
                              placeholder={t("practitioner_dashboard.add_follow_up_details")}
                              readOnly={isReadOnly}
                            />
                            <SpeechToTextInput
                              onChange={(transcribedText) => setFollowUpDetails((prevNotes) => prevNotes + ' ' + transcribedText)}  
                              isReadOnly={isReadOnly}
                            />
                            </div>
                          </div>
                        </>
                         )}
                        </div>
                        
                        {/* Therapist Treatment Summary Section */}
                        <div className="summary-section-wrapper">
                          <h4 className="summary-title">{t("practitioner_dashboard.treatment_summary")}</h4>

                          {/* Duration */}
                          <div className="summary-field">
                            <input
                              type="text"
                              className="summary-input"
                              value={duration}
                              onChange={(e) => setDuration(e.target.value)}
                              placeholder={t("practitioner_dashboard.enter_duration")}
                              readOnly={isReadOnly}
                            />
                          </div>

                          {/* Cost */}
                          {/* <div className="summary-field">
                            <input
                              type="text"
                              className="summary-input"
                              value={cost}
                              onChange={(e) => setCost(e.target.value)}
                              placeholder={t("practitioner_dashboard.enter_cost")}
                              readOnly={isReadOnly}
                            />
                          </div> */}

                          {/* Techniques Used */}
                          <div className="summary-field">
                            <div className="textarea-wrapper">
                              <textarea
                                className="summary-textarea"
                                value={techniques}
                                onChange={(e) => setTechniques(e.target.value)}
                                placeholder={t("practitioner_dashboard.enter_techniques")}
                                readOnly={isReadOnly}
                              />
                              <SpeechToTextInput
                                onChange={(transcribedText) => setTechniques((prev) => prev + ' ' + transcribedText)}
                                isReadOnly={isReadOnly}
                              />
                            </div>
                          </div>

                          {/* Comments */}
                          <div className="summary-field">
                            <div className="textarea-wrapper">
                              <textarea
                                className="summary-textarea"
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                placeholder={t("practitioner_dashboard.enter_comments")}
                                readOnly={isReadOnly}
                              />
                              <SpeechToTextInput
                                onChange={(transcribedText) => setComments((prev) => prev + ' ' + transcribedText)}
                                isReadOnly={isReadOnly}
                              />
                            </div>
                          </div>

                          {/* Self Care Recommendations */}
                          <div className="summary-field">
                            <div className="textarea-wrapper">
                              <textarea
                                className="summary-textarea"
                                value={selfCare}
                                onChange={(e) => setSelfCare(e.target.value)}
                                placeholder={t("practitioner_dashboard.enter_self_care")}
                                readOnly={isReadOnly}
                              />
                              <SpeechToTextInput
                                onChange={(transcribedText) => setSelfCare((prev) => prev + ' ' + transcribedText)}
                                isReadOnly={isReadOnly}
                              />
                            </div>
                          </div>
                        </div>
                        
                      </div>
                      <div className="custom-modal-actions">
                        <button className="doctor-dashboard-btn custom-close-btn" onClick={() => setIsModalOpen(false)}>{t("practitioner_dashboard.close")}</button>

                        {/* Conditional logic for Save and Mark as Completed buttons */}
                        {!isReadOnly && ['AP', 'PW'].includes(selectedAppointment.status) && (
                          <>
                            {/* Save Notes & Details button */}
                            {/* <button className="doctor-dashboard-btn custom-save-btn" onClick={handleSaveNotesAndDetails}>Save Notes & Details</button> */}

                            {/* Mark as Completed button */}
                            <button className=" doctor-dashboard-btn custom-complete-btn" onClick={handleMarkAsCompleted}>{t("practitioner_dashboard.mark_as_completed")}</button>
                          </>
                        )}

                        {/* Download PDF button (always available) */}
                        {/* <button className="doctor-dashboard-btn custom-save-btn" onClick={handleDownloadPDF}>{t("practitioner_dashboard.download_pdf")}</button> */}

                        {/* Send via Email button (always available) */}
                        {/* <button className="doctor-dashboard-btn custom-save-btn" onClick={() => setEmailModalOpen(true)}>Send via Email</button> */}
                      </div>
              </div>
              
              {/* Email Pop-up Modal */}
              {isEmailModalOpen && (
                <div className="custom-email-modal">
                  <div className="custom-email-modal-content">
                    <h3>Send Treatment Plan via Email</h3>
                    <input
                      type="email"
                      placeholder="Enter Patient's Email"
                      value={patientEmail}
                      onChange={(e) => setPatientEmail(e.target.value)}
                    />
                    <div className="custom-email-modal-actions">
                      {/* <button className="doctor-dashboard-btn" onClick={sendEmail}>Send</button> */}
                      <button className="doctor-dashboard-btn" onClick={() => setEmailModalOpen(false)}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
  }
      </div>
  );
};

export default DoctorDashboard;
